import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Mail, MessageSquare, ArrowUpRight, ArrowDownLeft,
  Clock, Download, Filter, Search, Smartphone, FileDown,
  Upload, Edit3, GitBranch, ExternalLink
} from 'lucide-react';
import client from '../api/client';
import { adminApi } from '../api/admin';

const ACTIVITY_CONFIG = {
  doc_generated: { icon: FileText, color: 'bg-blue-100 text-blue-600', label: 'Document Generated' },
  email_sent: { icon: Mail, color: 'bg-green-100 text-green-600', label: 'Email Sent' },
  sms_sent: { icon: Smartphone, color: 'bg-emerald-100 text-emerald-600', label: 'SMS Sent' },
  sms_received: { icon: Smartphone, color: 'bg-teal-100 text-teal-600', label: 'SMS Received' },
  portal_message: { icon: MessageSquare, color: 'bg-purple-100 text-purple-600', label: 'Portal Message' },
  stage_change: { icon: GitBranch, color: 'bg-orange-100 text-orange-600', label: 'Stage Changed' },
  note_added: { icon: Edit3, color: 'bg-yellow-100 text-yellow-600', label: 'Note Added' },
  doc_uploaded: { icon: Upload, color: 'bg-indigo-100 text-indigo-600', label: 'Document Uploaded' },
  doc_downloaded: { icon: Download, color: 'bg-gray-100 text-gray-600', label: 'Document Downloaded' },
};

const PAGE_SIZE = 30;

export default function ActivityLog() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [recordTypeId, setRecordTypeId] = useState('');
  const [recordTypes, setRecordTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    adminApi.getRecordTypes().then(r => setRecordTypes(r.data.record_types || [])).catch(() => {});
  }, []);

  useEffect(() => { loadData(); }, [page, filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (filter !== 'all') params.types = filter;
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (recordTypeId) params.record_type_id = recordTypeId;
      const { data } = await client.get('/communication-log/global', { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('types', filter);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (recordTypeId) params.set('record_type_id', recordTypeId);
      const { data } = await client.get(`/communication-log/global/export?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity_log_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  const handleDownload = async (item) => {
    if (!item.file_id) return;
    try {
      const { data } = await client.get(`/documents/${item.file_id}/download`);
      window.open(data.url, '_blank');
    } catch (err) {}
  };

  const formatDate = (ts) => {
    return new Date(ts).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Activity Log</h1>
          <p className="text-sm text-gray-500">All communications and activity across records • HIPAA audit trail</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <Filter className="w-4 h-4" /> Filters
          </button>
          <button onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800">
            <FileDown className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && loadData()}
                  placeholder="Subject, email, record..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Record Type</label>
              <select value={recordTypeId} onChange={e => setRecordTypeId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                <option value="">All Types</option>
                {recordTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); setRecordTypeId(''); setFilter('all'); setPage(0); }}
              className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700">Clear</button>
            <button onClick={() => { setPage(0); loadData(); }}
              className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700">Apply</button>
          </div>
        </div>
      )}

      {/* Type Filters */}
      <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1">
        {['all','doc_generated','email_sent','sms_sent,sms_received','portal_message','stage_change','note_added','internal_chat'].map(v => {
          const labels = { all: 'All', doc_generated: 'Documents', email_sent: 'Emails', 'sms_sent,sms_received': 'SMS', portal_message: 'Portal', stage_change: 'Stage Changes', note_added: 'Notes', internal_chat: 'Internal Chat' };
          return (
            <button key={v} onClick={() => { setFilter(v); setPage(0); }}
              className={`px-3 py-1.5 text-xs rounded-full whitespace-nowrap transition ${
                filter === v ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
              }`}>
              {labels[v]}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400">No activity found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Activity</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Record</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Details</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">By</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map(item => {
                const config = ACTIVITY_CONFIG[item.activity_type] || { icon: Clock, color: 'bg-gray-100 text-gray-500', label: item.activity_type };
                const Icon = config.icon;
                return (
                  <tr key={item.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${config.color}`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-xs font-medium text-gray-900">{config.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => navigate(`/records/${item.record_type_slug}/${item.record_id}`)}
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                        {item.reference_number || item.record_title || 'View'}
                        <ExternalLink className="w-3 h-3" />
                      </button>
                      {item.record_type_name && (
                        <span className="text-[10px] text-gray-400 block">{item.record_type_name}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {item.subject && <p className="text-xs text-gray-600 truncate">{item.subject}</p>}
                      {item.to_address && <p className="text-[10px] text-gray-400">To: {item.to_address}</p>}
                      {item.file_name && (
                        <button onClick={() => handleDownload(item)}
                          className="flex items-center gap-1 text-[10px] text-blue-600 hover:underline mt-0.5">
                          <FileText className="w-3 h-3" />{item.file_name}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">
                        {item.performer_first_name ? item.performer_first_name + ' ' + (item.performer_last_name || '') : 'System'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(item.created_at)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100">
            <span className="text-xs text-gray-400">
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </span>
            <div className="flex gap-1">
              <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                className="px-3 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}
                className="px-3 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
